import cx from 'classnames';
import React from 'react';
import { translate } from '../../../common/intl';
import useStyles, { KENDO_DATA_GRID_SHELL_EMPTY_HEIGHT } from './KendoDataGridShell.styles';

export interface KendoDataGridShellColumn {
  id: string
  title: React.ReactNode
  width?: number | string
}

export const toKendoDataGridShellColumns = (
  columns: Array<{
    id?: string
    field?: string
    title?: React.ReactNode
    width?: number | string
    isHidden?: boolean
  }>,
): KendoDataGridShellColumn[] => columns
  .filter((column) => Boolean(column.title) && !column.isHidden)
  .map((column) => ({
    id: String(column.id ?? column.field ?? column.title),
    title: column.title,
    width: column.width,
  }));

interface ColumnGroupProps {
  columns: KendoDataGridShellColumn[]
}

const ColumnGroup: React.FC<ColumnGroupProps> = ({ columns }) => (
  <colgroup>
    {columns.map((column) => (
      <col key={column.id} style={column.width != null ? { width: column.width } : undefined} />
    ))}
  </colgroup>
);

interface KendoDataGridShellProps {
  columns: KendoDataGridShellColumn[]
  children?: React.ReactNode
  withBottomButton?: boolean
  interactive?: boolean
  dataCy?: string
}

const KendoDataGridShell: React.FC<KendoDataGridShellProps> = ({
  columns,
  children,
  withBottomButton,
  interactive = false,
  dataCy,
}) => {
  const classes = useStyles();
  const hasRows = React.Children.count(children) > 0;

  return (
    <div
      className={cx('k-widget', 'k-grid', classes.grid, {
        [classes.empty]: !hasRows,
        [classes.withBottomButton]: withBottomButton,
        [classes.nonInteractive]: !interactive,
      })}
      style={hasRows ? undefined : { height: KENDO_DATA_GRID_SHELL_EMPTY_HEIGHT, maxHeight: '50vh' }}
      data-cy={dataCy}
    >
      <div className="k-grid-header">
        <div className="k-grid-header-wrap">
          <table>
            <ColumnGroup columns={columns} />
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.id} className="k-header">
                    <span className="k-cell-inner">
                      <span className="k-link">
                        <span className="k-column-title">{column.title}</span>
                      </span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
      </div>
      <div className="k-grid-container" style={{ textAlign: hasRows ? undefined : 'center', overflow: 'hidden' }}>
        <div className="k-grid-content" style={{ textAlign: hasRows ? undefined : 'center', overflow: 'hidden' }}>
          <table>
            <ColumnGroup columns={columns} />
            <tbody>
              {hasRows ? children : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="k-cell-empty"
                    style={{ textAlign: 'center', overflow: 'hidden' }}
                  >
                    {translate('table.empty.records')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KendoDataGridShell;
